/**
 * Interface Stockfish WASM dans un Web Worker.
 *
 * V1 :
 *  - Instancie le worker à la demande (lazy).
 *  - Envoie des commandes UCI textuelles.
 *  - Parse `info depth ... score (cp|mate) ... pv ...` et `bestmove ...`.
 *
 * Multi-thread : nécessite `crossOriginIsolated === true` (cf. .htaccess COOP/COEP
 * ARCHITECTURE.md §3.4). Sinon le worker tourne en single-thread (lent mais ok).
 *
 * Le binaire `stockfish.js` est copié dans `public/stockfish/` au build via
 * `scripts/setup-stockfish.mjs`.
 *
 * Bug A4 / hotfix MVP2.5 (2026-05-17) :
 *  Le package `stockfish.wasm` (niklasf/stockfish.wasm v0.10) n'expose PAS de
 *  `self.onmessage` global câblé sur UCI quand il est chargé directement comme
 *  Web Worker dédié. L'API officielle est :
 *    Stockfish().then(sf => { sf.addMessageListener(...); sf.postMessage('uci'); })
 *  après inclusion via `<script>` ou `importScripts()`.
 *  On charge donc un wrapper `stockfish-bridge.js` (généré par
 *  `scripts/setup-stockfish.mjs`) qui ponte `self.onmessage` ↔ `sf.postMessage`.
 *  Sans bridge : `init()` reste en pending éternel → UI Game Autopsy figée à 0/0.
 */

export interface EvalResult {
  depth: number;
  scoreCp: number | null;
  scoreMate: number | null;
  pv: string;
  bestmove: string | null;
}

/**
 * Résultat d'un coup joué par l'engine en mode adversaire (Practice Arena T2).
 *
 * `bestmove` : coup UCI (ex "e2e4" ou "e7e8q" pour promotion). Toujours présent
 *              sauf si Stockfish ne trouve pas de coup légal (mat/pat) — auquel
 *              cas `null` (rarissime, géré au call-site comme fin de partie).
 * `ponder`   : coup suivant prédit (UCI), peut être absent.
 */
export interface PlayMoveResult {
  bestmove: string | null;
  ponder: string | null;
}

/**
 * Cache-bust de l'URL du worker bridge. `app/public/stockfish/*` est servi par
 * Apache avec `cache-control: public, max-age=31536000, immutable` (cf.
 * `.htaccess`). Sans query param, le navigateur de Léon retient l'ancien
 * bridge indéfiniment même après un fix prod — `new Worker(url)` ne re-valide
 * pas le cache HTTP.
 *
 * Convention : bumper `WORKER_BRIDGE_VERSION` à CHAQUE modification du
 * BRIDGE_SOURCE dans `app/scripts/setup-stockfish.mjs`. Format
 * `YYYY-MM-DD-<slug>` pour rester lisible dans les logs.
 *
 * Historique :
 *  - 2026-05-18-mainscript-url-or-blob : fix `URL.createObjectURL(undefined)`
 *    sur stockfish.worker.js (pthread workers fils). Cf. PR fix(autopsy).
 */
const WORKER_BRIDGE_VERSION = '2026-05-18-mainscript-url-or-blob';
const WORKER_URL = `/stockfish/stockfish-bridge.js?v=${WORKER_BRIDGE_VERSION}`;

/**
 * Timeout d'init (handshake `uciok`). Doit couvrir :
 *  - téléchargement WASM ~350 kB (déjà en cache après 1er load, mais 1er load
 *    sur réseau lent peut prendre ~5s)
 *  - compilation Stockfish dans le worker
 *  - handshake UCI (`uci` → `uciok`)
 *
 * Calibration empirique :
 *  - iPad Safari single-thread : ~8s
 *  - Chrome desktop multi-thread : <1s
 *  - **Galaxy Tab A7 Lite (MT8768T, 3-4GB RAM, Chrome 148)** : 40-50s
 *    observés en prod (Léon, 2026-05-17). Le device de référence MVP2+ est
 *    bas de gamme avec ARM Cortex-A53/A75 hybride à 2.0 GHz — la compilation
 *    WASM Stockfish 16 NNUE est CPU-intensive et peut excéder 30s.
 *
 * P2 vague C'.2 : bump de 30_000 → 60_000ms. Marge × 1.2-1.5 sur le pire
 * observé pour absorber les ralentissements navigateur (autres onglets,
 * thermal throttling tablette en charge). Au pire Léon attend 60s pour voir
 * le message d'erreur — c'est UX dégradé mais permet à l'init de réussir
 * dans le cas nominal lent.
 */
const INIT_TIMEOUT_MS = 60_000;

/**
 * Timeout d'une eval `go depth N`. Depth 16 NNUE tourne ~1.5s multi-thread,
 * ~6s single-thread sur device moderne. Sur Galaxy Tab A7 Lite single-thread
 * (cas crossOriginIsolated=false), on observe 15-25s. 60s = marge × 2-3 pour
 * absorber les positions complexes en milieu de partie où Stockfish creuse
 * profond. Si dépassé c'est que le worker est deadlock — on throw plutôt
 * que de figer l'autopsie entière.
 *
 * P2 C'.2 : aligné sur INIT_TIMEOUT_MS pour cohérence et marge low-end.
 */
const ANALYZE_TIMEOUT_MS = 60_000;

/**
 * Erreur typée levée quand l'init Stockfish échoue (Worker constructor throw
 * sur Safari iPad sans COOP/COEP, fetch WASM 404, handshake `uciok` jamais
 * reçu). Cible privilégiée pour `instanceof` côté UI — l'heuristique
 * string-based `isStockfishInitError()` n'est plus que le filet en bout
 * de chaîne (cas où l'erreur viendrait d'un autre chemin imprévu).
 */
export class StockfishInitError extends Error {
  /** Stage où l'init a échoué — utile pour le logging backend. */
  readonly stage: 'worker_constructor' | 'worker_error_event' | 'uciok_timeout' | 'analyze_not_ready';

  constructor(stage: StockfishInitError['stage'], message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'StockfishInitError';
    this.stage = stage;
  }
}

/**
 * Heuristique string-based maintenue en fallback défensif pour les erreurs
 * qui n'auraient pas transité par `StockfishInitError` (cas exotiques :
 * exception levée dans `analyze()` avant init, ou dependency upstream qui
 * jette son propre type). Le call-site UI doit privilégier
 * `err instanceof StockfishInitError`.
 */
export function isStockfishInitError(err: unknown): boolean {
  if (err instanceof StockfishInitError) return true;
  if (!(err instanceof Error)) return false;
  const m = err.message.toLowerCase();
  return (
    m.includes('worker') ||
    m.includes('stockfish') ||
    m.includes('wasm') ||
    m.includes('engine not initialized')
  );
}

export class StockfishEngine {
  private worker: Worker | null = null;
  private ready = false;

  isCrossOriginIsolated(): boolean {
    return typeof self !== 'undefined' && self.crossOriginIsolated === true;
  }

  async init(): Promise<void> {
    if (this.worker) return;
    if (!this.isCrossOriginIsolated()) {
      console.warn('[engine] crossOriginIsolated=false — Stockfish runs in single-thread fallback.');
    }
    // P2 C'.2 : trace timing pour diagnostiquer les inits lents Galaxy Tab.
    // Sans ces traces, on ne sait pas si on a timeout sur compile WASM,
    // network DL, ou bridge handshake. Avec, on peut tuner INIT_TIMEOUT_MS
    // empiriquement et voir si un device particulier dérive.
    const initStartedAt = performance.now();
    try {
      this.worker = new Worker(WORKER_URL);
    } catch (cause) {
      throw new StockfishInitError(
        'worker_constructor',
        `Stockfish worker constructor failed: ${cause instanceof Error ? cause.message : 'unknown'}`,
        { cause },
      );
    }
    await new Promise<void>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        const elapsedMs = Math.round(performance.now() - initStartedAt);
        this.worker?.removeEventListener('message', onMessage);
        // P2 C'.2 : message d'erreur enrichi avec elapsed, UA, isolation
        // état. Permet à `ErrorMessageWithDebug` d'afficher un payload utile
        // pour diagnostiquer post-fait sans accès remote debug Galaxy Tab.
        const ua = typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 100) : 'no-ua';
        const isolated = typeof self !== 'undefined' && self.crossOriginIsolated === true;
        reject(
          new StockfishInitError(
            'uciok_timeout',
            `Stockfish handshake 'uciok' not received within ${INIT_TIMEOUT_MS}ms (elapsed=${elapsedMs}ms, crossOriginIsolated=${isolated}, UA=${ua}). Probable cause: WASM compilation too slow on this device, or worker bridge broken.`,
          ),
        );
      }, INIT_TIMEOUT_MS);

      const onMessage = (e: MessageEvent<string>) => {
        if (typeof e.data !== 'string') return;
        if (e.data === 'uciok') {
          const elapsedMs = Math.round(performance.now() - initStartedAt);
          // P2 C'.2 : log succès aussi (pas seulement échec) — permet de
          // surveiller la dérive perf au cours du temps en regardant les
          // logs console quand Léon teste.
          console.log(`[engine] Stockfish ready after ${elapsedMs}ms`);
          clearTimeout(timeoutId);
          this.ready = true;
          this.worker?.removeEventListener('message', onMessage);
          resolve();
        }
      };
      this.worker!.addEventListener('message', onMessage);
      this.worker!.addEventListener(
        'error',
        (err) => {
          const elapsedMs = Math.round(performance.now() - initStartedAt);
          clearTimeout(timeoutId);
          reject(
            new StockfishInitError(
              'worker_error_event',
              `Stockfish worker error event after ${elapsedMs}ms: ${err.message || 'unknown'} (filename=${err.filename ?? 'none'}, lineno=${err.lineno ?? 'none'})`,
              { cause: err },
            ),
          );
        },
        { once: true },
      );
      this.worker!.postMessage('uci');
    });
  }

  async analyze(fen: string, depth: number = 18): Promise<EvalResult> {
    if (!this.worker || !this.ready) {
      throw new StockfishInitError(
        'analyze_not_ready',
        'Engine not initialized — call init() first',
      );
    }
    return new Promise((resolve, reject) => {
      let scoreCp: number | null = null;
      let scoreMate: number | null = null;
      let pv = '';
      let lastDepth = 0;

      const timeoutId = setTimeout(() => {
        this.worker?.removeEventListener('message', onMessage);
        reject(
          new Error(
            `Stockfish analyze() timeout after ${ANALYZE_TIMEOUT_MS}ms (depth=${depth}, fen=${fen.slice(0, 30)}...)`,
          ),
        );
      }, ANALYZE_TIMEOUT_MS);

      const onMessage = (e: MessageEvent<string>) => {
        const line = e.data;
        if (typeof line !== 'string') return;
        const infoMatch = line.match(/^info .*depth (\d+).*score (cp|mate) (-?\d+).*pv (.+)$/);
        if (infoMatch) {
          lastDepth = parseInt(infoMatch[1]!, 10);
          if (infoMatch[2] === 'cp') {
            scoreCp = parseInt(infoMatch[3]!, 10);
            scoreMate = null;
          } else {
            scoreMate = parseInt(infoMatch[3]!, 10);
            scoreCp = null;
          }
          pv = infoMatch[4]!;
        }
        const bestmoveMatch = line.match(/^bestmove (\S+)/);
        if (bestmoveMatch) {
          clearTimeout(timeoutId);
          this.worker?.removeEventListener('message', onMessage);
          resolve({ depth: lastDepth, scoreCp, scoreMate, pv, bestmove: bestmoveMatch[1] ?? null });
        }
      };

      this.worker!.addEventListener('message', onMessage);
      this.worker!.postMessage(`position fen ${fen}`);
      this.worker!.postMessage(`go depth ${depth}`);
    });
  }

  /**
   * Fait jouer un coup à Stockfish en mode adversaire (Practice Arena T2).
   *
   * Différences avec `analyze()` :
   *  - Force un ELO d'adversaire (UCI_LimitStrength + UCI_Elo) ou Skill Level
   *    en fallback si levelElo < 1320 (limite basse Stockfish 16 NNUE).
   *  - Recherche en `go movetime <ms>` (temps fixe, pas profondeur).
   *  - Restaure ensuite le mode pleine force (UCI_LimitStrength=false) après
   *    le coup — important si la même instance est réutilisée pour `analyze()`
   *    en review post-partie.
   *
   * Mapping levelElo → UCI :
   *  - 800-1319  → UCI_LimitStrength=false + Skill Level 0..3
   *  - 1320-2850 → UCI_LimitStrength=true + UCI_Elo=clamp(levelElo, 1320, 2850)
   *  - >2850     → clamp 2850
   *
   * @param fen FEN courante (avant le coup engine)
   * @param levelElo Niveau cible (800..2850 — bornes Léon : 800..2500)
   * @param timeMs Temps de réflexion en ms (300..30000 raisonnable, on
   *               applique un clamp soft)
   *
   * @throws StockfishInitError si engine non initialisé
   * @throws Error en cas de timeout
   */
  async playMove(fen: string, levelElo: number, timeMs: number): Promise<PlayMoveResult> {
    if (!this.worker || !this.ready) {
      throw new StockfishInitError(
        'analyze_not_ready',
        'Engine not initialized — call init() first',
      );
    }
    const clampedTime = Math.max(100, Math.min(60_000, Math.round(timeMs)));
    // Marge × 2 + 10s pour absorber compile WASM + variance navigateur low-end
    // (cf. ANALYZE_TIMEOUT_MS calibration P2 C'.2 Galaxy Tab).
    const playTimeoutMs = Math.max(clampedTime * 2 + 10_000, 15_000);

    const { useUciElo, uciEloValue, skillLevelValue } = mapLevelEloToUci(levelElo);

    return new Promise((resolve, reject) => {
      let bestmove: string | null = null;
      let ponder: string | null = null;

      const timeoutId = setTimeout(() => {
        this.worker?.removeEventListener('message', onMessage);
        reject(
          new Error(
            `Stockfish playMove() timeout after ${playTimeoutMs}ms ` +
              `(level=${levelElo}, time=${clampedTime}ms, fen=${fen.slice(0, 30)}...)`,
          ),
        );
      }, playTimeoutMs);

      const onMessage = (e: MessageEvent<string>): void => {
        const line = e.data;
        if (typeof line !== 'string') return;
        const m = line.match(/^bestmove (\S+)(?: ponder (\S+))?/);
        if (m) {
          // `bestmove (none)` arrive quand l'engine ne trouve aucun coup
          // (mat/pat). On normalise en null pour que le call-site puisse
          // détecter la fin de partie sans parsing string.
          bestmove = m[1] === '(none)' ? null : (m[1] ?? null);
          ponder = m[2] ?? null;
          clearTimeout(timeoutId);
          this.worker?.removeEventListener('message', onMessage);
          // Reset force-pleine après usage (le mode review profond reste possible).
          this.worker?.postMessage('setoption name UCI_LimitStrength value false');
          this.worker?.postMessage('setoption name Skill Level value 20');
          resolve({ bestmove, ponder });
        }
      };

      this.worker!.addEventListener('message', onMessage);

      // Configure le niveau cible
      if (useUciElo) {
        this.worker!.postMessage('setoption name UCI_LimitStrength value true');
        this.worker!.postMessage(`setoption name UCI_Elo value ${uciEloValue}`);
      } else {
        this.worker!.postMessage('setoption name UCI_LimitStrength value false');
        this.worker!.postMessage(`setoption name Skill Level value ${skillLevelValue}`);
      }

      this.worker!.postMessage(`position fen ${fen}`);
      this.worker!.postMessage(`go movetime ${clampedTime}`);
    });
  }

  destroy(): void {
    this.worker?.terminate();
    this.worker = null;
    this.ready = false;
  }
}

/**
 * Calcule les options UCI à appliquer pour un niveau ELO cible.
 *
 * Stockfish 16 NNUE supporte `UCI_Elo` natif sur la range 1320-3190. En-dessous
 * de 1320, on bascule sur le mode `Skill Level 0..3` qui dégrade l'évaluation
 * d'une manière différente (probabilité plus haute de "voir" un coup non
 * optimal). 4 paliers : 800→0, 1000→1, 1100→2, 1200→3.
 *
 * Exporté pour tests Vitest isolés.
 */
export function mapLevelEloToUci(levelElo: number): {
  useUciElo: boolean;
  uciEloValue: number;
  skillLevelValue: number;
} {
  if (levelElo >= 1320) {
    return {
      useUciElo: true,
      uciEloValue: Math.max(1320, Math.min(2850, Math.round(levelElo))),
      skillLevelValue: 20,
    };
  }
  // Mapping ELO < 1320 → skill level 0..3
  // 800 → 0, 1000 → 1, 1100 → 2, 1200..1319 → 3
  let skillLevel = 0;
  if (levelElo >= 1200) skillLevel = 3;
  else if (levelElo >= 1100) skillLevel = 2;
  else if (levelElo >= 1000) skillLevel = 1;
  else skillLevel = 0;
  return {
    useUciElo: false,
    uciEloValue: 1320,
    skillLevelValue: skillLevel,
  };
}
